import {
  Divider,
  Stack,
  Typography
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DOMPurify from "dompurify";
import { useTranslation } from "react-i18next";
import {
  NotificationBox,
  DescriptionTypography,
  NotificationItem
} from "./Notification.styles";
import { NotificationProps } from "./Notification.types";
import {
  NAVBAR_ITEMS,
  NOTIFICATION_TYPE
} from "../../consts";
import { formatDate } from "../../services";
import { useNavbarStore } from "../../stores";
import { ButtonDigited } from "../Button/Button.variants";
import { Icon } from "../Icon";
import { Link } from "../Link";

export function Notification({
  contents,
  onClick=() => {},
  onClickLink,
  onMarkAllAsRead
}: NotificationProps) {
  const theme = useTheme();
  const { i18n, t } = useTranslation();
  const setNavbarItem = useNavbarStore(state => state.setNavbarItem);
  const hasUnread = Boolean(contents?.some(n => n.isRead === "N"));

  return (
    <NotificationBox
      container
      gap={ 3 }
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={ {
          padding: theme.spacing(3, 2.62, 3),
          width: "100%"
        } }
      >
        <Typography
          tabIndex={ 0 }
          fontWeight= "500"
          fontSize= "16px"
          lineHeight= "21px"
          sx={ {
            color: theme.customColors.textPrimary,
            fontWeight: "500",
            [theme.breakpoints.down("sm")]: {
              fontSize:"14px",
              fontWeight: 700,
              lineHeight:"18px"
            }
          } }
        >
          { t("notify_text") }
        </Typography>
        { onMarkAllAsRead && hasUnread && (
          <ButtonDigited
            typology="tertiary"
            buttonSize="sm"
            onClick={ onMarkAllAsRead }
          >
            { t("mark_all_as_read") }
          </ButtonDigited>
        ) }
      </Stack>
      {
        contents && contents?.length > 0 ? (
          contents.map((content, index) => {
            const notificationType = detectNotificationType(content.eventName);
            return (
              <NotificationItem
                className={ content.isRead !== "N" ? "displayed" : "" }
                key={ `notificationItem-${index}` }
                container
                sx={ {
                  cursor:"pointer",

                  ".icon path": {
                    fill: notificationType.color
                  }
                } }
                onClick={ () => { onClick(content)} }
              >
                <Icon
                  aria-hidden="true"
                  icon={ notificationType.icon }
                  height={ "20px" }
                  width={ "20px" }
                />
                <DescriptionTypography
                  tabIndex={ 0 }
                  aria-label={ content.description + formatDate(content.createdDate || "", i18n.language) }
                >
                  <div className={ "date" }>
                    { formatDate(content.createdDate || "", i18n.language) }
                  </div>
                  <Typography fontSize={ 14 } dangerouslySetInnerHTML={ { __html: DOMPurify.sanitize(content.description, { ADD_ATTR: ["target"] }) } } />
                </DescriptionTypography>
                { notificationType.linkText && notificationType.navBarItem && notificationType.link ? (
                  <Link
                    tabIndex={ 0 }
                    href={ notificationType.link }
                    target={ "_self" }
                    rel="noreferrer"
                    onClick={ () => {
                      setNavbarItem(notificationType.navBarItem);
                      onClickLink();
                    } }
                  >
                    { t(notificationType.linkText) }
                  </Link>
                ) : <div/> }
              </NotificationItem>
            );
          })
        ) : (
          <Typography
            tabIndex={ 0 }
            sx={ { padding: theme.spacing(3, 2.62, 3) } }
          >
            { t("empty_notify") }
          </Typography>
        )
      }
      <Divider
        aria-hidden="true"
        sx={ {
          padding: "1rem 0",
          width: "100%"
        } }
      />
    </NotificationBox>
  );

  function detectNotificationType(type: string) {
    switch (type) {
    case NOTIFICATION_TYPE.REMINDER:
      return {
        color: theme.customColors.textPrimary,
        icon: "Icons_obbligatorio"
      };
    case NOTIFICATION_TYPE.SUGGEST:
      return {
        color: theme.customColors.accentVariantB,
        icon: "Icons_invito"
      };
    case NOTIFICATION_TYPE.BADGE:
      return {
        color: theme.customColors.accentVariantB,
        icon: "Icons_modulo-completato",
        link: "/profile",
        linkText: "go_profile",
        navBarItem: NAVBAR_ITEMS.NONE
      };
    case NOTIFICATION_TYPE.ENROLL:
      return {
        color: theme.customColors.accentVariantB,
        icon: "Icons_invito"
      };
    case NOTIFICATION_TYPE.LEARN_REM:
      return {
        color: theme.customColors.textPrimary,
        icon: "Icons_scadenza-e-edizioni"
      };
    case NOTIFICATION_TYPE.RESET:
      return {
        color: theme.customColors.textPrimary,
        icon: "Icons_obbligatorio",
        link: "/profile",
        linkText: "go_profile",
        navBarItem: NAVBAR_ITEMS.NONE
      };
    case NOTIFICATION_TYPE.WELCOME:
      return {
        color: theme.customColors.accentVariantB,
        icon: "Icons_modulo-completato",
        link: "/profile",
        linkText: "go_profile",
        navBarItem: NAVBAR_ITEMS.NONE
      };
    case NOTIFICATION_TYPE.WELCOME_NP:
      return {
        color: theme.customColors.accentVariantB,
        icon: "Icons_modulo-completato",
        link: "/profile",
        linkText: "go_profile",
        navBarItem: NAVBAR_ITEMS.NONE
      };
    default:
      return {
        color: theme.customColors.textPrimary,
        icon: "Icons_obbligatorio"
      };
    }
  }
}
